<template lang="pug">
.image-container
  v-img(
    v-if="axiosLoadedImage"
    :alt="alt",
    :aspect-ratio="aspectRatio",
    :contain="contain",
    :content-class="contentClass",
    :dark="dark",
    :eager="eager",
    :gradient="gradient",
    :height="height",
    :lazy-src="lazySrc",
    :light="light",
    :max-height="maxHeight",
    :max-width="maxWidth",
    :min-height="minHeight",
    :min-width="minWidth",
    :options="options",
    :position="position",
    :sizes="sizes",
    :src="axiosLoadedImage",
    :srcset="srcset",
    :transition="transition",
    :width="width",
    @error="$emit('error', $event)",
    @load="$emit('load', $event)",
    @loadstart="$emit('loadstart', $event)"
  )

</template>

<script>
import axios from 'axios';

export default {
  name: 'VathosAxiosImage',
  props: {
    // These are optional properties that can be passed to the vuetify component
    alt: {
      type: String,
      default: undefined,
    },
    aspectRatio: {
      type: [String, Number],
      default: undefined,
    },
    contain: {
      type: Boolean,
      default: false,
    },
    contentClass: {
      type: String,
      default: undefined,
    },
    dark: {
      type: Boolean,
      default: false,
    },
    eager: {
      type: Boolean,
      default: false,
    },
    gradient: {
      type: String,
      default: undefined,
    },
    height: {
      type: [String, Number],
      default: undefined,
    },
    lazySrc: {
      type: String,
      default: undefined,
    },
    light: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: [String, Number],
      default: undefined,
    },
    maxWidth: {
      type: [String, Number],
      default: undefined,
    },
    minHeight: {
      type: [String, Number],
      default: undefined,
    },
    minWidth: {
      type: [String, Number],
      default: undefined,
    },
    options: {
      type: Object,
      default: () => ({
        root: undefined,
        rootMargin: undefined,
        threshold: undefined,
      }),
    },
    position: {
      type: String,
      default: 'center center',
    },
    sizes: {
      type: String,
      default: undefined,
    },
    src: {
      type: [String, Object],
      default: '',
      required: true,
    },
    srcset: {
      type: String,
      default: undefined,
    },
    transition: {
      type: [Boolean, String],
      default: 'fade-transition',
    },
    width: {
      type: [String, Number],
      default: undefined,
    },
    showPlaceholderImage: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // We are loading the default image, in case no image can be found
      axiosLoadedImage: undefined,

    };
  },
  watch: {
    async src(changedValue) {
      if (changedValue) {
        await this.loadImageWithAxios(changedValue);
      } else {
        this.clearImageObjectUrl();
      }
    },
  },
  mounted() {
    if (this.showPlaceholderImage) {
      this.axiosLoadedImage = require(`@/assets/images/default-image.jpg`);
    }
    if (!this.src) {
      this.clearImageObjectUrl();
      return;
    }
    this.loadImageWithAxios(this.src);
  },
  beforeDestroy() {
    // We have to destroy the object URLs to prevent memory leak when the user navigates away from the page
    // because @load fires too early in some cases causing a race condition
    if (this.axiosLoadedImage) URL.revokeObjectURL(this.axiosLoadedImage);
    this.axiosLoadedImage = undefined;
  },
  methods: {
    async loadImageWithAxios(imageSrc) {
      // Because we are sending the image id preponed with /blobs and sometimes we can have
      // undefined value for the id, we need to check if the image source has the string undefine in it
      // and stop executing the function because it will throw 404 ( the url will be /blobs/undefined in most cases )
      // also we will clear the object url if present
      if (imageSrc.includes('undefined')) {
        if (this.axiosLoadedImage) URL.revokeObjectURL(this.axiosLoadedImage);
        this.axiosLoadedImage = undefined;
        return;
      }
      this.$emit('axiosLoadStarted');
      try {
        const options = {url: imageSrc, method: 'get', responseType: 'blob'};
        const response = await axios.request(options);
        if (response.data) {
          this.axiosLoadedImage = URL.createObjectURL(response.data);
        }
      } catch (error) {
        console.log('Image load error: ', error);
      }
      this.$emit('axiosLoadFinished');
    },
    clearImageObjectUrl() {
      // I'm creating this function so it can be called from the outside to clear the image object url
      // Because sometimes the component is not destroyed but we need the image to be empty
      if (this.axiosLoadedImage) {
        URL.revokeObjectURL(this.axiosLoadedImage);
        this.axiosLoadedImage = require(`@/assets/images/default-image.jpg`);
      }
    },
  },

};
</script>
